import type { SkillAccessibility, SkillDraft } from "@/shared/domain/skills";

// ---------------------------------------------------------------------------
// Input type
//
// `createClientTool` casts raw LLM JSON to this shape with `params as T`, so
// nothing here is statically verified at runtime — `coerceToSkillDraft` is the
// only normalization layer between this input and the draft handed to the
// editor. It lives in its own module so both the tool's `execute` and the
// approval preview can normalize identically, without the tool ⇄ confirmation
// import cycle a shared definition in `createSkill.ts` would create.
// ---------------------------------------------------------------------------

export interface CreateSkillInput {
  name?: string;
  description?: string;
  body?: string;
  accessibility?: string;
  tags?: unknown[];
}

const ACCESSIBILITIES: readonly SkillAccessibility[] = [
  "private",
  "org",
  "org-editable",
];

function coerceTags(raw: unknown[] | undefined): string[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const seen = new Set<string>();
  const tags: string[] = [];
  for (const value of raw) {
    if (typeof value !== "string") {
      continue;
    }
    const trimmed = value.trim();
    if (trimmed === "") {
      continue;
    }
    const key = trimmed.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    tags.push(trimmed);
  }
  return tags;
}

/**
 * Normalize LLM-emitted input into a {@link SkillDraft} to seed the create
 * editor, or return `null` when the proposal carries nothing to prefill (no
 * name, description, or body).
 *
 * This is lenient by design: unlike the old "persist on approve" path, the
 * editor — not this function — is the validation seam now. A name with spaces,
 * an over-length description, or a missing field is still seeded so the user
 * lands in the editor with the model's draft and the editor's own validation
 * prompts them to fix it. Only structural noise is normalized away here:
 * outer whitespace is trimmed, blank scalars are omitted, tags are trimmed /
 * deduped (case-insensitively) / stripped of non-strings, and an unrecognized
 * accessibility is dropped so the editor keeps its `private` default rather
 * than rendering an out-of-range Select value.
 *
 * Both the tool's `execute` and the approval preview call this so the card the
 * user approves and the form they land in show the same draft.
 */
export function coerceToSkillDraft(input: CreateSkillInput): SkillDraft | null {
  const draft: SkillDraft = {};

  const name = typeof input.name === "string" ? input.name.trim() : "";
  if (name !== "") {
    draft.name = name;
  }
  const description =
    typeof input.description === "string" ? input.description.trim() : "";
  if (description !== "") {
    draft.description = description;
  }
  // Bodies are Markdown, so trimming outer whitespace is safe — interior
  // structure (blank lines between blocks, list markers, fenced code) carries
  // the meaning and survives.
  const body = typeof input.body === "string" ? input.body.trim() : "";
  if (body !== "") {
    draft.body = body;
  }
  // Only a recognized tier is seeded. An empty / omitted / mis-typed value
  // leaves `accessibility` unset so the editor stays on its `private` default;
  // unlike the old persist path we don't error on a typo, since the user picks
  // the tier in the editor before saving.
  if (
    typeof input.accessibility === "string" &&
    ACCESSIBILITIES.includes(input.accessibility as SkillAccessibility)
  ) {
    draft.accessibility = input.accessibility as SkillAccessibility;
  }
  const tags = coerceTags(input.tags);
  if (tags.length > 0) {
    draft.tags = tags;
  }

  // Nothing worth opening the editor for — let `execute` surface an error so
  // the model re-proposes with content rather than navigating to a blank form.
  if (
    draft.name === undefined &&
    draft.description === undefined &&
    draft.body === undefined
  ) {
    return null;
  }
  return draft;
}
